import {FC, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {useAppSelector} from '../../../../setup/redux/hooks'
import {
  branchUsersFilterDataChange,
  deleteBranchUser,
  getBranchUsers,
  selectBranchUsersFilter,
  selectBranchUsersList,
  updateUserBranchStatus,
  setShowEditBranchUserModal,
} from '../redux/BranchRedux'
import {PaginateModel} from '../../../shared/models/PaginateModel'
import Paginator from '../../../components/Paginator'
import moment from 'moment'

import Tooltip from 'react-tooltip-lite'
import {UserBranchModel} from '../models/UserBranchModel'
import {Language, USER_STATUS} from '../../../shared/constants'
import {selectUser} from '../../../modules/auth/redux/AuthRedux'
import {Modal, Button} from 'react-bootstrap'
import EditUserBranchModal from './EditUserBranchModal'

type Props = {
  className?: string
}

const UserBranchTable: FC<Props> = (props) => {
  const intl = useIntl()
  const isJapanese = intl.locale === Language.JAPANESES
  const dispatch = useDispatch()
  const didRequest = useRef(false)
  const data: UserBranchModel[] = useAppSelector(selectBranchUsersList)
  const filter: PaginateModel = useAppSelector(selectBranchUsersFilter)
  const currentUser = useAppSelector(selectUser)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserBranchModel | null>(null)

  useEffect(() => {
    if (!didRequest.current) {
      dispatch(getBranchUsers())
      didRequest.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChanged = (targetPage: number) => {
    dispatch(branchUsersFilterDataChange({...filter, page: targetPage}))
  }

  const handleDeleteClick = (user: UserBranchModel) => {
    setUserToDelete(user)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    if (userToDelete) {
      dispatch(deleteBranchUser({userId: userToDelete.id}))
    }
    setShowDeleteConfirm(false)
    setUserToDelete(null)
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
    setUserToDelete(null)
  }

  const renderItemActions = (user: UserBranchModel) => {
    // Don't show actions for current user
    if (user.id === currentUser?.id) {
      return null
    }

    let actions = []

    // For UN_VERIFY users, only show delete button
    if (user.status === USER_STATUS.UN_VERIFY) {
      actions.push(
        <Tooltip
          key='delete-tooltip'
          content={<div>{intl.formatMessage({id: 'SYSTEM.DELETE'})}</div>}
          className='tooltips'
        >
          <button
            key='delete'
            className='btn btn-icon btn-light-danger btn-sm'
            onClick={() => handleDeleteClick(user)}
          >
            <i className='bi bi-trash fs-4'></i>
          </button>
        </Tooltip>
      )
      return <div className='d-flex justify-content-end flex-shrink-0'>{actions}</div>
    }

    if (user.status === USER_STATUS.ACTIVE) {
      actions = [
        <Tooltip
          key='status-tooltip'
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.INACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-danger btn-sm me-1'
            onClick={() => {
              dispatch(updateUserBranchStatus({userIds: [user.id], status: USER_STATUS.INACTIVE}))
            }}
          >
            <i className='bi bi-x-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    } else {
      actions = [
        <Tooltip
          key='status-tooltip'
          content={<div>{intl.formatMessage({id: 'Scheduler.STATUS.ACTIVE'})}</div>}
          className='tooltips'
        >
          <button
            key='status'
            className='btn btn-icon btn-light-success btn-sm me-1'
            onClick={() => {
              dispatch(updateUserBranchStatus({userIds: [user.id], status: USER_STATUS.ACTIVE}))
            }}
          >
            <i className='bi bi-check-circle fs-4'></i>
          </button>
        </Tooltip>,
      ]
    }

    // Add edit button
    actions.push(
      <Tooltip
        key='edit-tooltip'
        content={<div>{intl.formatMessage({id: 'SYSTEM.EDIT'})}</div>}
        className='tooltips'
      >
        <button
          key='edit'
          className='btn btn-icon btn-light-primary btn-sm me-1'
          onClick={() => dispatch(setShowEditBranchUserModal({visible: true, user}))}
        >
          <i className='bi bi-pencil fs-4'></i>
        </button>
      </Tooltip>
    )

    // Add delete button
    actions.push(
      <Tooltip
        key='delete-tooltip'
        content={<div>{intl.formatMessage({id: 'SYSTEM.DELETE'})}</div>}
        className='tooltips'
      >
        <button
          key='delete'
          className='btn btn-icon btn-light-danger btn-sm'
          onClick={() => handleDeleteClick(user)}
        >
          <i className='bi bi-trash fs-4'></i>
        </button>
      </Tooltip>
    )

    return <div className='d-flex justify-content-end flex-shrink-0'>{actions}</div>
  }

  const renderBrandUserItem = (user: UserBranchModel) => {
    const isCurrentUser = user.id === currentUser?.id
    return (
      <tr
        key={user.id}
        style={isCurrentUser ? {backgroundColor: '#f5f5f5', opacity: 0.7} : undefined}
      >
        <td> {user.id.toString()}</td>
        <td> {isJapanese ? user.lastName : user.firstName} </td>
        <td> {isJapanese ? user.firstName : user.lastName} </td>
        <td> {user.email} </td>
        <td> {user.branchName} </td>
        <td> {user.totalOrders} </td>
        <td>
          <span
            className={`badge badge-light-${
              user.status === USER_STATUS.ACTIVE ? 'success' : 'danger'
            } d-inline-flex align-items-center`}
          >
            {intl.formatMessage({id: 'BRANCH.STATUS.' + user.status})}
          </span>
        </td>
        <td> {moment(user.createdAt).format(process.env.REACT_APP_DATE_FORMAT)} </td>
        <td>{renderItemActions(user)}</td>
      </tr>
    )
  }
  return (
    <>
      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table table-hover table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
          {/* begin::Table head */}
          <thead>
            <tr className='text-start text-muted fw-bolder fs-7 text-uppercase gs-0'>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.ID'})}</th>
              <th>
                {isJapanese
                  ? intl.formatMessage({id: 'BRANCH.USER.TABLE.LAST_NAME'})
                  : intl.formatMessage({id: 'BRANCH.USER.TABLE.FIRST_NAME'})}
              </th>
              <th>
                {isJapanese
                  ? intl.formatMessage({id: 'BRANCH.USER.TABLE.FIRST_NAME'})
                  : intl.formatMessage({id: 'BRANCH.USER.TABLE.LAST_NAME'})}
              </th>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.EMAIL'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.BRAND_NAME'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.TOTAL_ORDERS'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.STATUS'})}</th>
              <th>{intl.formatMessage({id: 'BRANCH.USER.TABLE.CREATE_AT'})}</th>
              <th className='text-end'>{intl.formatMessage({id: 'BRANCH.USER.TABLE.ACTION'})}</th>
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>{data?.map((item: any) => renderBrandUserItem(item))}</tbody>
          {/* end::Table body */}
        </table>
        {/* end::Table */}
      </div>
      <div className='d-flex justify-content-end pt-10'>
        <Paginator
          currentPage={filter.page}
          totalPages={filter.pageCount || 1}
          onPageChange={handlePageChanged}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'BRANCH.USER.DELETE_CONFIRM_TITLE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{intl.formatMessage({id: 'BRANCH.USER.DELETE_CONFIRM_MESSAGE'})}</p>
          <p className='text-warning fw-bold'>
            <i className='bi bi-exclamation-triangle me-2'></i>
            {intl.formatMessage({id: 'BRANCH.USER.DELETE_WARNING'})}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCancelDelete}>
            {intl.formatMessage({id: 'SYSTEM.CANCEL'})}
          </Button>
          <Button variant='danger' onClick={handleConfirmDelete}>
            {intl.formatMessage({id: 'SYSTEM.DELETE'})}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <EditUserBranchModal />
    </>
  )
}

export default UserBranchTable
